/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { Button, Modal } from "antd";
import { AnnouncementApi } from "api/announcement";
import { AnnouncementResponseType } from "api/announcement/type";
import EmptyComponent from "components/common/EmptyComponent";
import Loading from "components/common/Loading";
import { QueryKeys } from "constants/queryKeys";
import { useAppContext } from "contexts/app.context";
import { ModalTypeValue } from "enums/modalType";
import { handleCloseModal } from "helpers/handleModal";
import { ReactComponent as CloseIcon } from "assets/common/icons/icon_close.svg";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { AnnouncementItemType } from "types/announcement";
import { ModalAnnouncementStyle } from "./style";

function stripHtml(html?: string) {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function ModalAnnouncement() {
  const { state, dispatch } = useAppContext();
  const [selectedAnnouncement, setSelectedAnnouncement] =
    useState<AnnouncementItemType | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  const { data: response, isLoading } = useQuery<AnnouncementResponseType>(
    [QueryKeys.Announcement],
    {
      queryFn: async () => {
        return await AnnouncementApi.getAnnouncement({ page: 1, size: 100 });
      },
      staleTime: Infinity,
    }
  );

  const data = response?.data || [];
  const selectedId = selectedAnnouncement?.id;

  const selectedTimeText = useMemo(() => {
    if (!selectedAnnouncement?.start) return "";
    return moment(selectedAnnouncement.start).format("YYYY-MM-DD HH:mm");
  }, [selectedAnnouncement?.start]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setShowMobileDetail(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (state.openModal !== ModalTypeValue.ANNOUNCEMENT) {
      setSelectedAnnouncement(null);
      setShowMobileDetail(false);
      return;
    }
    if (data.length === 0) {
      setSelectedAnnouncement(null);
      setShowMobileDetail(false);
      return;
    }
    if (!selectedAnnouncement) {
      setSelectedAnnouncement(data[0]);
      setShowMobileDetail(false);
      return;
    }
    const isStillAvailable = data.some((item) => item.id === selectedId);
    if (!isStillAvailable) {
      setSelectedAnnouncement(data[0]);
      setShowMobileDetail(false);
    }
  }, [state.openModal, data, selectedAnnouncement, selectedId]);

  return (
    <Modal
      open={state.openModal === ModalTypeValue.ANNOUNCEMENT}
      onCancel={() => handleCloseModal(state, dispatch)}
      centered
      closable={false}
      footer={null}
      width={800}
      css={css`
        .ant-modal-content {
          padding: 0 !important;
          border-radius: 15px;
          overflow: hidden;
        }
      `}
    >
      <div css={ModalAnnouncementStyle.index}>
        <div css={ModalAnnouncementStyle.header}>
          <p>公告</p>
          <Button
            type="text"
            css={ModalAnnouncementStyle.btnClose}
            icon={<CloseIcon />}
            onClick={() => handleCloseModal(state, dispatch)}
          ></Button>
        </div>
        {isLoading ? (
          <Loading center height={300} />
        ) : data.length === 0 ? (
          <div css={ModalAnnouncementStyle.empty}>
            <EmptyComponent />
          </div>
        ) : (
          <>
            {isMobile ? (
              showMobileDetail && selectedAnnouncement ? (
                <div css={ModalAnnouncementStyle.mobileDetail}>
                  <Button
                    type="text"
                    className="back"
                    onClick={() => setShowMobileDetail(false)}
                  >
                    ← 返回
                  </Button>
                  <div className="detail">
                    <div className="title">
                      {selectedAnnouncement.title || "-"}
                    </div>
                    {selectedTimeText ? (
                      <div className="time">{selectedTimeText}</div>
                    ) : null}
                    <div
                      className="content"
                      dangerouslySetInnerHTML={{
                        __html: selectedAnnouncement.content || "",
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div css={ModalAnnouncementStyle.mobileList}>
                  <div className="list">
                    {data.map((item, index) => {
                      const previewText = stripHtml(item.content);
                      const isActive = item.id === selectedId;
                      return (
                        <div
                          key={item.id ?? index}
                          className={`item${isActive ? " active" : ""}`}
                          onClick={() => {
                            setSelectedAnnouncement(item);
                            setShowMobileDetail(true);
                          }}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedAnnouncement(item);
                              setShowMobileDetail(true);
                            }
                          }}
                        >
                          <h3>{item.title || "-"}</h3>
                          <p>{previewText || "-"}</p>
                          <div className="time">
                            <span>
                              {item.start
                                ? moment(item.start).format("YYYY-MM-DD HH:mm")
                                : "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )
            ) : (
              <div css={ModalAnnouncementStyle.content}>
                <div css={ModalAnnouncementStyle.listPanel}>
                  <div className="list">
                    {data.map((item, index) => {
                      const previewText = stripHtml(item.content);
                      const isActive = item.id === selectedId;
                      return (
                        <div
                          key={item.id ?? index}
                          className={`item${isActive ? " active" : ""}`}
                          onClick={() => setSelectedAnnouncement(item)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedAnnouncement(item);
                            }
                          }}
                        >
                          <h3>{item.title || "-"}</h3>
                          <p>{previewText || "-"}</p>
                          <div className="time">
                            <span>
                              {item.start
                                ? moment(item.start).format("YYYY-MM-DD HH:mm")
                                : "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div css={ModalAnnouncementStyle.detailPanel}>
                  {selectedAnnouncement ? (
                    <div css={ModalAnnouncementStyle.detail}>
                      <div className="title">
                        {selectedAnnouncement.title || "-"}
                      </div>
                      {selectedTimeText ? (
                        <div className="time">{selectedTimeText}</div>
                      ) : null}
                      <div
                        className="content"
                        dangerouslySetInnerHTML={{
                          __html: selectedAnnouncement.content || "",
                        }}
                      />
                    </div>
                  ) : (
                    <div className="placeholder">
                      Select an announcement to view details.
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

export default ModalAnnouncement;
